import { BaseInterface } from '../../BaseInterface'

/**
 * BaseCache
 * ────────────────────────────────────────────────────────────────────
 * 메모리 기반 캐시 구현체로, `libs/DB/BaseDB`와 동일한 메서드 시그니처를 제공한다.
 * `BaseInterface`를 구현하여 `BaseDB`와 교체 가능한 계약을 보장한다.
 *
 * 설계 목표
 *  1. BaseDB와 호환되는 API 표면 — 호출부 변경 없이 교체 가능.
 *  2. 순수 인메모리 동작 — 비동기 초기화/IDB 트랜잭션 없음.
 *  3. TTL/만료/콜백 훅 등 BaseDB의 시맨틱을 최대한 보존.
 *
 * BaseDB와의 주요 차이점
 *  - IDB 관련 옵션(`version`, `indexList`, `skipPreload`, `autoInit`)은 의미가 없어 제거.
 *  - `isActive`는 생성 직후 `true`, `close()` 호출 시 `false`로 전환(쓰기/읽기 차단).
 *  - `load()` / `init()` 단계가 없으므로 `isLoaded()`와 `getDB()`는 즉시 동기적으로 해결.
 *  - 모든 비동기 메서드는 `Promise.resolve`/`Promise.reject`로 즉시 완료되나,
 *    시그니처는 Promise 기반을 유지해 BaseDB를 대체해도 await 흐름이 깨지지 않도록 함.
 *
 * 교체 예시
 *  - `new BaseDB('knownSender', { key: 'email', expireDuration: 60_000 })`
 *  - `new BaseCache('knownSender', { key: 'email', expireDuration: 60_000 })`
 *  두 인스턴스 모두 `getData/putData/deleteData/clear/...`를 동일하게 지원하므로
 *  필요 시 단순 생성자 교체만으로 영속성 여부를 전환할 수 있다.
 */

/**
 * 생성자 옵션.
 * BaseDBOptions와 동일한 이름/시맨틱을 사용한다.
 *
 * - `key`: 저장되는 레코드에서 키를 담을 속성명. 기본 `'key'`.
 * - `dataKey`: 저장되는 레코드에서 데이터 본문을 담을 속성명. 기본 `'data'`.
 * - `expireDuration`:
 *    - `number` (ms): 레코드 저장 시 기본 만료시간.
 *    - `null`: 만료 없음(영구).
 *    - `undefined`: 기본값(30일) 사용.
 * - `expireDurationForEmpty`:
 *    - `number` (ms): `getData` miss 시 생성되는 placeholder의 TTL.
 *      placeholder가 살아있는 동안에는 동일 키 조회에서 추가 작업 없이 `null`을 즉시 반환.
 *    - `null`: placeholder가 영구(만료 없음). `putData`로 덮어쓰기 전까지 계속 `null` 반환.
 *    - `undefined`: 기본값(3분) 사용.
 * - `getSaveData`: 저장 직전에 내부 표현을 가공하고 싶을 때 사용. 반환값이 dataMap에 저장된다.
 * - `beforeRemoveSaveData`: `putData`(덮어쓰기) / `deleteData` / `clear` 직전에 기존 값을 전달.
 *   Blob revoke 등 리소스 해제에 사용. 훅이 throw해도 후속 정리가 보장되도록 try/catch로 감싸 호출.
 * - `checkForList`: `getAllData`의 dataList에 포함할지 여부를 필터링. 반환이 `true`인 항목만 포함.
 * - `shouldDeferEvict`: `getData`가 만료된 실데이터 항목을 placeholder로 교체하기 직전에 호출.
 *   true 반환 시 `expireTime`을 `evictDeferMs`만큼 연장하고 cached 그대로 반환(evict 미룸).
 *   placeholder 만료에는 적용되지 않는다 — 연장 무의미.
 *   `maxDeferCount` 초과 시 무시되고 강제 evict.
 * - `evictDeferMs`: defer 시 expireTime 연장 폭(ms). 기본 60_000.
 * - `maxDeferCount`: 동일 키에서 연속 defer 가능한 최대 횟수. 기본 5.
 */
export interface BaseCacheOptions<T = any> {
  key?: string
  dataKey?: string
  expireDuration?: number | null
  expireDurationForEmpty?: number | null
  getSaveData?: (item: any, key?: string) => T
  beforeRemoveSaveData?: (item: T) => void
  checkForList?: (item: T, key: string) => boolean
  shouldDeferEvict?: (item: T) => boolean
  evictDeferMs?: number
  maxDeferCount?: number
}

/**
 * 내부 저장 레코드의 형태.
 * `[key]`: 키(문자열), `[dataKey]`: 데이터 본문, `expireTime`: 만료 시각(ms epoch),
 * `isExpired()`: 만료 여부를 런타임에 계산하는 클로저.
 *
 * 실제 구조는 `key`/`dataKey` 옵션에 따라 속성명이 바뀌기 때문에 index signature로 표현한다.
 */
interface CacheItem {
  expireTime?: number | null
  isExpired?: () => boolean
  [prop: string]: unknown
}

class BaseCache<T = any> implements BaseInterface<T> {
  cacheName: string

  /** 생성 직후 true. close() 호출 시 false. BaseDB와 동일한 플래그 시맨틱을 유지한다. */
  isActive: boolean

  /** BaseDB의 load() 완료 플래그에 대응. BaseCache는 초기화 단계가 없으므로 생성 직후 true. */
  loaded: boolean

  /** 레코드에서 키를 담을 속성명. */
  key: string

  /** 레코드에서 데이터 본문을 담을 속성명. */
  dataKey: string

  /** 개별 레코드의 기본 TTL(ms). null이면 영구. */
  expireDuration: number | null

  /** miss placeholder의 TTL(ms). null이면 영구. */
  expireDurationForEmpty: number | null

  /**
   * 실제 저장소. BaseDB에서는 IDB 캐시 역할이지만, BaseCache에서는 유일한 저장소다.
   * 외부에서 직접 조작하지 말고 반드시 메서드를 통해 접근해야 한다.
   */
  dataMap: Record<string, T>

  options: BaseCacheOptions<T>

  /** 키별 evict defer 카운터. `shouldDeferEvict`로 미뤄질 때마다 +1, 정상 evict / putData / touch 시 0. */
  private deferCountMap: Record<string, number> = {}

  private static readonly DEFAULT_EVICT_DEFER_MS = 60_000

  private static readonly DEFAULT_MAX_DEFER_COUNT = 5

  constructor(name: string, options: BaseCacheOptions<T> = {}) {
    this.cacheName = name
    this.options = options

    this.key = options.key ?? 'key'
    this.dataKey = options.dataKey ?? 'data'

    // expireDuration: 숫자(사용자 지정) / null(영구) / undefined(기본 30일)을 각각 구분.
    // BaseDB의 분기 패턴과 정확히 동일하게 유지해 시맨틱 일치를 보장한다.
    this.expireDuration = 30 * 24 * 60 * 60 * 1000
    if (options.expireDuration != null) {
      this.expireDuration = options.expireDuration
    } else if (options.expireDuration === null) {
      this.expireDuration = null
    }

    // expireDurationForEmpty: 위와 동일한 3분기 해석.
    this.expireDurationForEmpty = 3 * 60 * 1000
    if (options.expireDurationForEmpty != null) {
      this.expireDurationForEmpty = options.expireDurationForEmpty
    } else if (options.expireDurationForEmpty === null) {
      this.expireDurationForEmpty = null
    }

    this.dataMap = {}
    this.isActive = true
    this.loaded = true
  }

  /**
   * 레코드에 `isExpired()` 클로저를 부착한다.
   * 클로저가 `item.expireTime`을 참조 기반으로 읽으므로,
   * 이후 `expireTime`이 변경되어도 최신 값으로 평가된다.
   * (BaseDB와 동일한 패턴)
   */
  private applyExpireFunc = (item: CacheItem): CacheItem & { isExpired: () => boolean } => {
    const result = item as CacheItem & { isExpired: () => boolean }
    if (item.expireTime != null) {
      result.isExpired = () => item.expireTime! < Date.now()
    } else {
      result.isExpired = () => false
    }
    return result
  }

  /**
   * BaseDB.close() 대응.
   * `isActive`를 false로 바꿔 이후 읽기/쓰기를 차단한다.
   * 주의: dataMap은 유지되므로 재사용을 원한다면 별도 clear() 필요.
   */
  close = (): void => {
    this.isActive = false
  }

  /**
   * `beforeRemoveSaveData` 훅을 try/catch로 감싸 호출한다.
   * 훅이 throw해도 후속 정리(placeholder 할당 / dataMap 삭제 / 카운터 reset)가
   * 보장되도록 한다. 훅이 동일 분기에서 다시 throw → 무한 루프가 되는 시나리오 차단.
   */
  private callBeforeRemoveSaveData = (item: T): void => {
    if (this.options.beforeRemoveSaveData == null) return
    try {
      this.options.beforeRemoveSaveData(item)
    } catch (e) {
      console.warn(`BaseCache(${this.cacheName}): beforeRemoveSaveData threw`, e)
    }
  }

  /**
   * 데이터 변경 없이 `expireTime`만 갱신한다.
   * - `dataMap[key]`가 있을 때만 동작 (없으면 false 반환).
   * - `beforeRemoveSaveData` 훅 미트리거 — 데이터 자체는 그대로 유지된다.
   * - defer 카운터를 0으로 reset — 활성 키가 다음 만료 cycle에서 처음부터 defer 가능.
   *
   * 사용 예: inline 이미지 path를 사용 중일 때 호출해 cache TTL을 연장.
   * `expireTime` 갱신은 `applyExpireFunc`이 부착한 `isExpired` 클로저가
   * 참조 기반으로 읽기 때문에 별도 재부착 없이도 즉시 반영된다.
   */
  touch = (key: string, ttl?: number): boolean => {
    if (this.isActive !== true) return false
    const item = this.dataMap[key] as CacheItem | undefined
    if (item == null) return false

    const dur = ttl ?? this.expireDuration
    if (dur != null) {
      item.expireTime = Date.now() + dur
    }

    delete this.deferCountMap[key]
    return true
  }

  /**
   * 신규 추가. 동일 키가 이미 존재하면 IDB add()와 유사하게 reject한다.
   * BaseDB는 IDB의 ConstraintError를 그대로 전파하지만,
   * BaseCache는 동일 시맨틱을 제공하는 자체 Error를 사용한다.
   */
  addData = (key: string, data: unknown, expireDuration?: number): Promise<T | null> => {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    if (this.dataMap[key] != null) {
      return Promise.reject(new Error(`BaseCache(${this.cacheName}): duplicate key "${key}"`))
    }

    return Promise.resolve(this.writeRecord(key, data, expireDuration, false))
  }

  /**
   * 신규 추가 또는 기존 값 덮어쓰기.
   * 기존 값이 있으면 `beforeRemoveSaveData` 훅을 호출한 뒤 교체한다.
   */
  putData = (key: string, data: unknown, expireDuration?: number): Promise<T | null> => {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    return Promise.resolve(this.writeRecord(key, data, expireDuration, true))
  }

  /**
   * addData/putData의 공통 쓰기 루틴.
   * @param overwrite true면 기존 값에 대한 beforeRemoveSaveData 훅을 수행 후 덮어쓴다.
   */
  private writeRecord = (
    key: string,
    data: unknown,
    expireDuration: number | undefined,
    overwrite: boolean,
  ): T => {
    // 기존 값이 있을 때만 제거 훅 호출(메모리 리소스 해제 등).
    if (overwrite && this.dataMap[key] != null) {
      this.callBeforeRemoveSaveData(this.dataMap[key])
    }

    // 새 데이터로 덮어쓸 때 defer 카운터 reset — 새 cycle 시작.
    if (overwrite) {
      delete this.deferCountMap[key]
    }

    const newItem: CacheItem = {
      [this.key]: key,
      [this.dataKey]: data,
    }

    // 호출 인자 > 생성자 기본값 > 무제한의 우선순위로 만료시각을 설정.
    if (expireDuration != null) {
      newItem.expireTime = Date.now() + expireDuration
    } else if (this.expireDuration != null) {
      newItem.expireTime = Date.now() + this.expireDuration
    }

    let item: any = this.applyExpireFunc(newItem)

    // 저장 직전 변환 훅. 반환값이 최종적으로 dataMap에 저장된다.
    if (this.options.getSaveData != null) {
      item = this.options.getSaveData(newItem, key)
    }

    this.dataMap[key] = item
    return this.dataMap[key]
  }

  /**
   * 키에 해당하는 레코드를 삭제한다.
   * 기존 값이 있으면 `beforeRemoveSaveData` 훅을 호출한다.
   * 존재 여부와 무관하게 삭제는 성공(resolve(key))으로 간주 — BaseDB.deleteData와 동일.
   */
  deleteData = (key: string): Promise<string | null> => {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    if (this.dataMap[key] != null) {
      this.callBeforeRemoveSaveData(this.dataMap[key])
      delete this.dataMap[key]
      delete this.deferCountMap[key]
    }

    return Promise.resolve(key)
  }

  /**
   * 전체 레코드를 삭제한다.
   * 모든 값에 대해 `beforeRemoveSaveData` 훅을 순차 호출한 뒤 dataMap을 비운다.
   */
  clear = (): Promise<boolean | null> => {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    if (this.options.beforeRemoveSaveData != null) {
      Object.values(this.dataMap).forEach((value) => {
        this.callBeforeRemoveSaveData(value)
      })
    }

    this.dataMap = {}
    this.deferCountMap = {}
    return Promise.resolve(true)
  }

  /**
   * 키로 레코드를 조회한다.
   *
   * 반환 규칙(BaseDB와 동일)
   *  1. 실제 데이터(`dataKey` 속성 존재) + 만료 전 → 레코드 객체 전체 반환.
   *     호출자는 `result.data`(또는 dataKey에 해당하는 속성)로 본문을 읽는다.
   *  2. placeholder(`dataKey` 속성 없음) + 만료 전 → null 반환.
   *     "이미 miss로 확인된 키"로 간주하여 추가 작업 없이 null.
   *  3. 레코드 없음 or 만료됨 →
   *     a) `expireDurationForEmpty != null`일 때 placeholder를 생성해 dataMap에 저장한 뒤 null 반환.
   *     b) `expireDurationForEmpty == null`일 때도 동일하게 "영구 placeholder"를 기록.
   *        (BaseDB의 동작과 일치. 명시적으로 null을 지정하면 miss 캐싱 수명이 무제한이 됨을 의미.)
   *
   * 주의: 만료된 기존 레코드는 이 메서드 호출 시 placeholder로 대체되어 제거된다.
   */
  getData = (key: string): Promise<T | null> => {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const cached: any = this.dataMap[key]
    if (cached != null && (cached.isExpired == null || cached.isExpired() === false)) {
      if (this.dataKey in cached) {
        return Promise.resolve(cached)
      }
      // placeholder 히트 → "없음"이 캐시된 상태.
      return Promise.resolve(null)
    }

    // 만료된 실데이터 + shouldDeferEvict가 true → expireTime 연장 후 cached 반환.
    // placeholder 만료에는 적용 안 함(연장 무의미).
    // maxDeferCount 초과 시 무시되고 강제 evict로 진행.
    if (cached != null && this.dataKey in cached && this.options.shouldDeferEvict != null) {
      const deferCount = this.deferCountMap[key] ?? 0
      const maxDeferCount = this.options.maxDeferCount ?? BaseCache.DEFAULT_MAX_DEFER_COUNT
      if (deferCount < maxDeferCount) {
        let shouldDefer = false
        try {
          shouldDefer = this.options.shouldDeferEvict(cached) === true
        } catch (e) {
          console.warn(`BaseCache(${this.cacheName}): shouldDeferEvict threw`, e)
        }
        if (shouldDefer === true) {
          const deferMs = this.options.evictDeferMs ?? BaseCache.DEFAULT_EVICT_DEFER_MS
          ;(cached as CacheItem).expireTime = Date.now() + deferMs
          this.deferCountMap[key] = deferCount + 1
          return Promise.resolve(cached)
        }
      }
    }

    // miss 또는 만료 → placeholder 기록. defer 카운터 reset.
    delete this.deferCountMap[key]
    const placeholder: CacheItem = {
      [this.key]: key,
      expireTime:
        this.expireDurationForEmpty != null ? Date.now() + this.expireDurationForEmpty : null,
    }
    this.dataMap[key] = this.applyExpireFunc(placeholder) as unknown as T
    return Promise.resolve(null)
  }

  /**
   * 전체 레코드를 순회해 실제 데이터만 반환한다.
   *
   * - 만료된 항목은 lazily 정리(삭제)한 뒤 결과에서 제외.
   * - placeholder(데이터 본문 없음)도 결과 제외.
   * - `checkForList` 콜백이 있으면 dataList에 포함할지 필터링.
   *
   * 반환: `{ dataMap, dataList }` — dataMap은 내부 저장소 참조이므로
   * 호출자는 이를 조작해서는 안 된다(BaseDB의 관행과 동일).
   */
  getAllData = (): Promise<{ dataMap: Record<string, T>; dataList: T[] } | null> => {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const dataList: T[] = []

    Object.entries(this.dataMap).forEach(([k, item]) => {
      const anyItem = item as any

      if (anyItem?.isExpired != null && anyItem.isExpired() === true) {
        // 만료 항목은 제거 훅 실행 후 삭제.
        this.callBeforeRemoveSaveData(item)
        delete this.dataMap[k]
        delete this.deferCountMap[k]
        return
      }

      // placeholder(dataKey 없음)는 결과에서 제외.
      if (!(this.dataKey in anyItem)) {
        return
      }

      if (this.options.checkForList == null || this.options.checkForList(item, k) === true) {
        dataList.push(item)
      }
    })

    return Promise.resolve({ dataMap: this.dataMap, dataList })
  }

  /** BaseDB.isLoaded() 대응. BaseCache는 생성 직후 항상 true. */
  isLoaded = (): boolean => {
    return this.loaded
  }

  /**
   * BaseDB.getDB() 대응.
   * BaseDB에서는 IDB 오픈 완료를 기다리지만, BaseCache는 동기적으로 즉시 해결한다.
   * close() 이후에는 false를 반환.
   */
  getDB = (): Promise<boolean> => {
    return Promise.resolve(this.isActive)
  }
}

export default BaseCache
